import type {DoppeDtoAction, DoppeDtoActionViewType} from '../../client-server-common/types/doppe-dto-action';
import type {
    BymoPageProps,
    DoppeActionListItemClickBehaviour,
    DoppeDtoSpeedDialLinkViewerData
} from '../../client-server-common';
import type {BymoPageContextProps} from '../bymo-page/bymo-page-context-props';
import type {
    DoppeViewerModalsController
} from '../../components/doppe-viewer-modals-controller/doppe-viewer-modals-controller';
import type {NonNullishValue, ObservableValue, Values} from '@wix/devzai-utils-common';
import type {DoppeLinkSpec} from '../../client-server-common/types/doppe-action-cta-button';
import type {
    DoppeViewerPagesRoutesManager
} from '../../client-server-common/viewer-pages-routes-manager/doppe-viewer-pages-routes-manager';

import type {DoppeSearchItemViewer} from '../../client-server-common/types/doppe-dto-search-item-viewer-data';
import type {DoppeActionWidgetPopupMode} from '../../client-server-common/types/doppe-action-widget-popup-mode';
import type {
    DoppeActionGoalType,
    DoppeActionsSourceType,
    DoppeActionType,
    WidgetLockController,
    WidgetRenderingContextType
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeViewerActionsList} from '../../components/doppe-viewer-actions-list/doppe-viewer-actions-list';
import type {DoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import type {BymoPageViewerContextData} from '../../components/bymo-page-viewer-context/bymo-page-viewer-context';
import type {DoppeDtoCheckoutIntent,} from '../../client-server-common/types/doppe-dto-checkout-intent';
import type {DoppeViewerLang} from "../../client-server-common/doppe-viewer/doppe-viewer-lang";
import type {DoppeLockActionTypePaymentOrderMatchStatus} from '../../client-server-common/doppe-action-types/doppe-lock-action-type-payment/doppe-lock-action-type-payment-checkout-ref';

export const DoppeResolvedLinkType = {
    Internal: 'internal',
    External: 'external',
} as const;

export type DoppeResolvedLinkType = Values<typeof DoppeResolvedLinkType>;

export interface DoppeResolvedLink {
    url: string;
    type: DoppeResolvedLinkType;
}

export interface IDoppeViewerActionContext<PROPS extends NonNullishValue = any> {

    action: DoppeDtoAction<PROPS>;

    sourceType: DoppeActionsSourceType;

    observableCurrentItemIdToRender: ObservableValue<string | null> | undefined;
    observableIsRenderingInnerState: ObservableValue<boolean>;
    actionsListController: DoppeViewerActionsList | undefined;

    getActionType(): DoppeActionType<PROPS>;

    modalsController: DoppeViewerModalsController;

    lockController: WidgetLockController | null;

    getActionViewType(): DoppeDtoActionViewType;

    getWidgetRenderingContextType(): WidgetRenderingContextType;

    getActionIndex(): number;

    __apiHealthCheck(): Promise<void>;

    reportGoalAchieved(options: {
        innerItemId?: string,
        innerItemPosition?: number,
        overrideAction?: {
            actionId: string;
            actionIndex: number;
            actionType: string;
            actionName?: string;
        },
        goalType?: DoppeActionGoalType,
    }): void;

    reportCtaButtonClicked(options: {
        innerItemId?: string,
        innerItemPosition?: number,
        overrideAction?: {
            actionId: string;
            actionIndex: number;
            actionType: string;
            actionName?: string;
        },
        goalType?: DoppeActionGoalType,
        buttonId: string,
        buttonIndex?: number;
    }): void;

    reportCtaButtonViewed(options: {
        innerItemId?: string,
        innerItemPosition?: number,
        overrideAction?: {
            actionId: string;
            actionIndex: number;
            actionType: string;
            actionName?: string;
        },
        goalType?: DoppeActionGoalType,
        buttonId: string,
        buttonIndex?: number;
    }): void;

    reportButtonClicked(options: {
        buttonName: string,
    }): void;

    unlockLockAction(metaData?: any): Promise<void>;

    isLockAction(): boolean;

    startUnlocking(onUnlocked: () => void, showUnlockPreview: boolean, overrideActionData?: {
        action: DoppeDtoAction<any>,
        index: number,
    }): Promise<void>;

    unlockAction(options: {
        unlockedMetaData?: any;
        unlockedCallback?: () => void
        overrideActionData?: {
            action: DoppeDtoAction<any>,
            index: number,
        },
        isUnlockedFromCookie?: boolean;
    }): Promise<boolean>;

    reportInnerItemViewed(innerItemdId?: string, innerItemIndex?: number, overrideAction?: {
        actionId: string;
        actionIndex: number;
        actionType: string;
    }): void;

    reportInnerItemPreviewed(options: {
        innerItemId: string,
        innerItemPosition?: number,
        overrideAction?: {
            actionId: string;
            actionIndex: number;
            actionType: string;
        },
        previewType: DoppeActionListItemClickBehaviour;
    }): void;

    reportActionImpression(): void;

    reportOnlyInnerItemClicked(options: {
        innerItemId: string,
        innerItemPosition?: number,
        overrideAction?: {
            actionId: string;
            actionIndex: number;
            actionType: string;
        },
        goalType: DoppeActionGoalType,
    }): void;

    navigateToUrl(targetUrl: string, newTab: boolean): void;

    previewAction(actionId: DoppeDtoAction.Id, options?: {
        closeCallback?: () => void;
        showRootBackButtonInFullScreen?: boolean;
        showRootCloseButtonInFullScreen?: boolean;
        openedFromSearch?: boolean;
        listItemId?: string;
        widgetPopupMode?: DoppeActionWidgetPopupMode;
        actionIndex?: number;
        startNewContext: boolean;
        showHeader?: boolean;
    }): void;


    navigateToSearchItem(
        searchItem: DoppeSearchItemViewer,
        options: {
            newTab: boolean;
            viewerLang: DoppeViewerLang;
        }
    ): void;

    reportSearchItemLinkClicked(searchItem: DoppeSearchItemViewer, viewerLang: DoppeViewerLang): void;


    performContextCallback(actionCallback: (options: {
        clientServices: DoppeViewerClientServices;
        viewerContextData: BymoPageViewerContextData;
        bymoPageProps: BymoPageProps;
    }) => Promise<void>): Promise<void>;

    /**
     * @deprecated
     */
    performViewerOnlyCallback(actionCallback: () => Promise<void>): Promise<void>;

    resolveSpeedDialLinkFromCode(linkCode: string): Promise<DoppeDtoSpeedDialLinkViewerData | undefined>;

    navigateToSpeedDialLink(speedDialLink: DoppeDtoSpeedDialLinkViewerData, newTab: boolean): void;

    addContact(
        contactInfo: {
            email?: string;
            phone?: string;
            contactName?: string;
            zipCode?: string;
            address?: string;
        }
    ): Promise<void>;

    submitContactForm(
        contactInfo: {
            email?: string;
            phone?: string;
            contactName?: string;
            zipCode?: string;
            address?: string;
            listId?: string;
            message?: string;
        }
    ): Promise<void>;

    submitWixForm(
        options: {
            formId: string;
            formData: Record<string, any>;
        }
    ): Promise<void>;

    submitCodeToUnlock(
        codeInfo: {
            code: string;
        }
    ): Promise<boolean>;

    submitOrderToUnlock(
        orderInfo: {
            orderNumber: string;
            email: string;
        }
    ): Promise<DoppeLockActionTypePaymentOrderMatchStatus>;

    redirectToTargetUrl(
        options?: {
            additionalMessage?: string;
            countdownSeconds?: number;
        }
    ): void;

    showNotification(
        options?: {
            message?: string;
            actionButton?: {
                label: string;
                onClick: () => void;
            };
        }
    ): void;

    setActionSettingsOverrides(overrides: PROPS): void;

    toggleActionView(expand: boolean): void;

    canBeToggled(): boolean;

    createStripePaymentIntent(options: {
        innerItemId?: string,
        checkoutIntent?: DoppeDtoCheckoutIntent;
    }): Promise<{ clientSecret: string; paymentIntentId: string }>;

    saveCheckoutIntent(options: {
        checkoutIntent: DoppeDtoCheckoutIntent;
        uouCheckoutData: DoppeDtoCheckoutIntent.UouCheckoutData;
        paymentIntentId: string;
        innerItemId?: string,
    }): Promise<void>;

    createWixStoresCheckoutUrl(options: {
        productId: string,
        variantId?: string,
        productProperties?: { [key: string]: string },
        customTextFields?: { [key: string]: string },
    }): Promise<{ url: string }>;

    getBiIdFields(): {
        actionId: string;
        actionType: string;
        pageId: string;
        linkId: string;
        hypeUserId: string;
        pageType: string;
    };

    getGoogleMapsApiKey(): string;

    /**
     * Get the current page ID for analytics tracking.
     * Returns DoppeDtoInnerPageKnownIds.Home when on the home page, or undefined if not available.
     */
    getCurrentInnerPageId(): string | undefined;
    
    /**
     * Resolves a DoppeLinkSpec to a URL with navigation type.
     * 
     * Returns `{ url, type }` where type is:
     * - 'internal': a relative path for router-based SPA navigation (e.g. `/post/slug`)
     * - 'external': a full URL for standard browser navigation (e.g. `https://...`)
     * 
     * For Entity link specs, when viewerLang and showMobileView are provided,
     * uses full navigation resolution (internal routing vs external URL based on
     * useHoppVertical*Pages flags). Without these options, falls back to
     * internal routing only.
     */
    resolveLinkSpecToUrl(
        linkSpec: DoppeLinkSpec,
        options?: {
            viewerLang?: DoppeViewerLang;
            showMobileView?: boolean;
        }
    ): DoppeResolvedLink | undefined;

    /**
     * Navigates to an internal page path using routing-based navigation.
     * Unlike navigateToUrl, this works in LivePreview mode (not prevented).
     * Use for paths resolved from DoppeLinkSpec.Entity or DoppeLinkSpec.Page.
     */
    navigateToInternalPage(path: string): void;
    
    /**
     * Get the bymoPageProps for this action context.
     */
    getBymoPageProps(): BymoPageProps;
    
    /**
     * Get the viewer pages routes manager.
     */
    getViewerPagesRoutesManager(): DoppeViewerPagesRoutesManager;
    
    /**
     * Get the bymoPageContextProps for this action context.
     * This contains business entities data, viewer features, etc.
     */
    getBymoPageContextProps(): BymoPageContextProps;
}